import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { logger } from '../utils/logger';
import { supabase } from '../lib/supabase';
import Footer from '../components/Footer';
import Header from '../components/Header';

declare global {
  interface Window {
    rewardful?: (action: string, data: { email: string }) => void;
  }
}

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const sessionId = searchParams.get('session_id');
  const [purchaseData, setPurchaseData] = useState(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true); // Loading state for middle section
  const today = new Date().toLocaleDateString();

  useEffect(() => {
    if (!sessionId || !user) {
      logger.error('Missing required payment data', { 
        hasSessionId: !!sessionId, 
        hasUser: !!user 
      });
      navigate('/pricing', { replace: true });
      return;
    }

    // Fetch purchase information from Supabase Edge function
    const fetchPurchaseData = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          throw new Error('Authentication required');
        }

        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-order-info`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
              'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({ sessionId: sessionId }),
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch purchase data');
        }

        const data = await response.json();
        setPurchaseData(data);
        logger.info('Fetched purchase data', { data });

        // Add Rewardful conversion tracking here
        if (window.rewardful && data.email) {
          window.rewardful('convert', { 
            email: data.email 
          });
        }        

      } catch (err) {
        logger.error('Error fetching purchase data', err);
        setError('Failed to load purchase details. Please contact support.');
      } finally {
        setLoading(false); // Set loading to false after fetching
      }
    };

    fetchPurchaseData();
  }, [sessionId, user, navigate]);

  // Top section (loads instantly)
  return (
    <div className="flex flex-col">
      <Header />
      <main className="px-20 md:px-40"> 
      <section className="relative overflow-hidden py-20 md:py-32 bg-background"> 
        <div className="min-h-screen py-12">
          <div className="container mx-auto px-4">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900">Thank You for Your Order!</h1>
              <div className="mt-4">
                {/* Basic success checkmark/animation */}
                <span className="text-blue-500 text-5xl">✔️</span>
              </div>
              {loading && <p className="mt-2">Loading your order details...</p>}
            </div>

            {/* Middle section (populates after verification) */}
            {error ? (
              <div className="max-w-2xl mx-auto bg-white p-8 rounded-lg">
                <div className="text-red-600">
                  <h3 className="text-lg font-semibold mb-2">{error}</h3>
                  <p className="text-sm">Session ID: {sessionId}</p>
                  <p className="text-sm mt-2">
                    Please contact support with this session ID for assistance.
                  </p>
                </div>
              </div>
            ) : (
              purchaseData && (
                <div className="max-w-2xl mx-auto bg-white p-8 rounded-lg">
                  <h2 className="text-2xl font-bold mb-4">Order Details</h2>
                  <div className="border-t border-b border-gray-200 py-4 my-4">
                    <div className="flex justify-between mb-2">
                      <span className="font-medium">Order Number</span>
                      <span>{purchaseData.orderNumber}</span>
                    </div>
                    <div className="flex justify-between mb-2">
                      <span className="font-medium">Order Email</span>
                      <span>{purchaseData.email}</span>
                    </div>
                    <div className="flex justify-between mb-2">
                      <span className="font-medium">Item</span>
                      <span>{purchaseData.plan}</span>
                    </div>
                    <div className="flex justify-between mb-2">
                      <span className="font-medium">Amount</span>
                      <span>${purchaseData.amount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium">Order Date</span>
                      <span>{today}</span>
                    </div>
                  </div>
                </div>
              )
            )}

            {/* Bottom section (loads last) */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
              <Link
                to="/dashboard"
                className="flex items-center px-6 py-2 border-2 border-blue-500 text-blue-500 rounded-lg hover:bg-blue-50 transition-colors"
              >
                Go to User Dashboard
              </Link>
            </div>
          </div>
        </div>
        </section>
      </main>
    <Footer />
    </div>
    
  );
}